import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react'
import { useNow } from '@/hooks/useNow'
import { trackUse } from '@/state/usage'
import { useKeyboardUp } from '@/hooks/useVisualViewport'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Check, Code2, History, Plus, Mic, X } from 'lucide-react'
import { api, engine, mutate } from '@/runtime'
import {
  useAgentSessions,
  useAllRuns,
  useNames,
  useResource,
  useSettings,
  useSuggestionsByIds,
} from '@/db/hooks'
import type {
  AgentMessage,
  AgentSessionResource,
  AgentSessionSpec,
  AgentSessionStatus,
} from '@/kinds/agentsession'
import type { SuggestionResource } from '@/kinds/suggestion'
import { acceptSuggestion, applyForAgent, describeOp, dismissSuggestion } from '@/lib/suggestions'
import { undoWithFlash } from '@/lib/undo'
import { useAssistantEnabled } from '@/components/Suggestions'
import { useStream } from '@/state/stream'
import { useAllProjects, useMergeCards } from '@/db/hooks'
import {
  mergedWords,
  mergeStartedAt,
  mergeView,
  mergingWords,
  mergeWords,
  type MergeView,
} from '@/lib/mergeQueue'
import { useUi } from '@/state/ui'
import { useVoiceToday } from '@/voice/today'
import { APP_ROUTES, runVoiceCommand } from '@/voice/commands'
import { applySuggestionIds, useAgentState } from '@/voice/agent'
import type { AgentToolResult } from '@/db/types'
import { CHAT_COMMANDS, type ChatCommand } from '@/lib/chatCommands'
import { useScope } from '@/state/scope'
import { ALL, DEFAULT_WORKSPACE_SLUG } from '@/kinds/workspace/schema'
import { Button, CHIP_CLASS, IconButton, SideSheet, cx } from '@/components/ui'
import type { AgentRunResource } from '@/kinds/agentrun'
import { useFactory } from '@/kinds/agentrun/components'
import { RunTag } from '@/components/RunStatus'
import { ToolCallDetail } from '@/components/ToolCall'
import { appLineOf, newsParts } from '@/kinds/agentrun/news'
import { QUICK_CHAT, liveLead } from '@/lib/lead'
import { PrimeAsleep } from '@/components/PrimeAsleep'
import { AgentList, AgentsLayout, QUICK_CHAT_ID } from '@/components/AgentList'
import { MdLite } from '@/lib/mdlite'
import { isFresh } from '@/lib/entrance'
import { announceAgent } from '@/lib/agentToast'
import { PrimeComposer, type ComposerMessage } from '@/components/PrimeComposer'
import { PICTURE_ONLY } from '@/components/MessagePictures'
import { SessionPictures } from '@/components/SessionPictures'

/**
 * One conversation, and the only way in: you land in a chat and talk. A new
 * chat has no session until you send something; sending makes one and stays on
 * the same screen. The reply is written into the page as it arrives, so there
 * is nothing to wait in front of.
 */
export const ChatView = () => {
  const { id } = useParams({ strict: false }) as { id?: string }
  const navigate = useNavigate()
  // `ask` is a question handed over from elsewhere (Search's "Ask Prime"), put in the box unsent
  const search = useSearch({ strict: false }) as { fresh?: boolean; ask?: unknown; quick?: boolean }
  // (the router reads `?ask=12` as a number)
  const ask = search.ask == null ? undefined : String(search.ask)
  const sessions = useAgentSessions()
  const enabled = useAssistantEnabled()
  const settings = useSettings()
  const scope = useScope((s) => s.ws)
  const ws = scope === ALL ? DEFAULT_WORKSPACE_SLUG : scope
  const session = useResource<AgentSessionSpec, AgentSessionStatus>(id) as AgentSessionResource | undefined
  // the quick chat: the in-app brain's long-lived thread (?quick), voice's and the page panel's
  const prime = sessions.find((s) => s.spec.prime && !s.deleted_at)
  // Prime is the lead (lib/lead.ts): /agents, and a question handed over with
  // ?ask=, go to its thread with the words in its box
  const allRuns = useAllRuns()
  const lead = liveLead(allRuns)
  // with no Prime session live, /agents says so and offers to start it
  // (components/PrimeAsleep.tsx, the words kept) rather than slipping into the quick chat
  const factory = useFactory()
  // (once the runs are read and the factory known, so a cold start never flashes it before the live one is found)
  const asleep = !id && !search.fresh && !search.quick && factory.known && allRuns !== undefined && !lead
  useEffect(() => {
    if (id || search.fresh) return
    if (!search.quick) {
      if (lead)
        void navigate({
          to: '/agents/run/$id',
          params: { id: lead.id },
          search: (ask ? { ask } : {}) as never,
          replace: true,
        })
      return
    }
    if (!enabled) return
    if (prime) {
      void navigate({
        to: '/agents/$id',
        params: { id: prime.id },
        search: { ask } as never,
        replace: true,
      })
      return
    }
    let stale = false
    api
      .agentPrime(ws)
      .then(async ({ session: made }) => {
        await engine.sync()
        if (!stale)
          void navigate({
            to: '/agents/$id',
            params: { id: made.id },
            search: { ask } as never,
            replace: true,
          })
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [id, search.fresh, ask, search.quick, enabled, prime, lead, ws, navigate])
  const { titleOf } = useNames()

  const [text, setText] = useState(() => ask ?? '')
  const [sending, setSending] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const voice = useAgentState()
  const keyboardUp = useKeyboardUp()

  const streamed = useStream((s) => (id ? (s.text[id] ?? '') : ''))
  // acting happens on the device that asked, or two open devices would both do it
  const sentHere = useStream((s) => (id ? !!s.sentHere[id] : false))
  const step = useStream((s) => (id ? (s.step[id] ?? '') : ''))
  const messages = useMemo(() => session?.spec.messages ?? [], [session])
  const working = sending || !!session?.status.busy
  const archived = session?.status.state === 'archived'

  /*
   * Follow the conversation down, but never take the scroll off someone who has
   * moved away from the bottom to read something. Smooth scrolling on every
   * streamed word fights the finger, so the jump is instant and only happens
   * while we are already at the end.
   */
  const stick = useRef(true)
  useEffect(() => {
    const onScroll = () => {
      const from = document.documentElement.scrollHeight - window.scrollY - window.innerHeight
      stick.current = from < 120
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const toEnd = (smooth = false) => {
    if (!stick.current) return
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  }
  // a new turn is worth following; the words as they arrive are not worth a jolt
  // (the first time, before the first paint and without the glide, so a thread you come back to
  // opens at its end, not at its top; and no glide under a keyboard, where iOS drags the page about)
  const placed = useRef(false)
  useLayoutEffect(() => {
    stick.current = true
    if (!placed.current) {
      placed.current = !!session
      toEnd(false)
      return
    }
    requestAnimationFrame(() => toEnd(!keyboardUp))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, !!session])
  useEffect(() => {
    if (streamed) requestAnimationFrame(() => toEnd())
  }, [streamed])

  // the reply landed: drop the streamed copy so the stored turn is the one shown.
  // Only once the session is actually here and finished: while it is still on
  // its way to this device the words are all there is to show.
  const settled = !!session && !session.status.busy
  useEffect(() => {
    if (id && settled) useStream.getState().clear(id)
  }, [id, settled])

  const run = (c: ChatCommand) => {
    setText('')
    if (c.to) {
      void navigate({ to: c.to })
      return
    }
    if (c.prompt) void send(c.prompt).catch(() => setText(c.prompt ?? ''))
  }

  // `m` is the composer's message; a slash command's prompt is words only. Throws when it fails,
  // which keeps the composer's draft and pictures.
  const send = async (m: string | ComposerMessage) => {
    const { text: words, images } = typeof m === 'string' ? { text: m, images: [] } : m
    const content = words.trim()
    if ((!content && !images.length) || sending) return
    setSending(true)
    setErr(null)
    stick.current = true
    requestAnimationFrame(() => toEnd(true))
    trackUse('action:prime-message')
    try {
      if (session) {
        useStream.getState().markSent(session.id)
        await api.agentSend(session.id, content, images.length ? { images } : undefined)
      } else {
        const { session: made } = await api.agentStart({ workspace: ws, content })
        useStream.getState().markSent(made.id)
        await engine.sync()
        // same screen, new address: no page to be taken to
        void navigate({ to: '/agents/$id', params: { id: made.id }, replace: true })
      }
      // the message is the server's now: the box clears without waiting for the round trip
      void engine.sync()
    } catch (e) {
      setErr((e as Error).message)
      throw e
    } finally {
      setSending(false)
    }
  }

  // a turn may end with device tools to run (play, open, capture…): the device
  // that asked runs them through the same runner the desk uses and answers
  const today = useVoiceToday()
  const projects = useAllProjects()
  const ran = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!session || !sentHere || session.status.busy) return
    const last = messages[messages.length - 1]
    const calls = last?.role === 'assistant' ? (last.tool_calls ?? []) : []
    if (!last || calls.length === 0) return
    const key = `${session.id}:${messages.length}`
    if (ran.current.has(key)) return
    ran.current.add(key)
    void (async () => {
      const results: AgentToolResult[] = []
      for (const tc of calls) {
        let said = ''
        const say = (t: string) => {
          said = t
          useUi.getState().showFlash(t, 2500)
        }
        try {
          const args = (tc.args ?? {}) as Record<string, unknown>
          const str = (v: unknown) => (typeof v === 'string' ? v : '')
          const env = {
            say,
            today,
            projects: projects ?? [],
            open: (view: string) => {
              const route = APP_ROUTES[view]
              if (!route) return false
              void navigate({ to: route })
              return true
            },
          }
          if (tc.name === 'apply_suggestions')
            said = await applySuggestionIds(Array.isArray(args.ids) ? args.ids.map(String) : [])
          else if (tc.name === 'capture')
            await runVoiceCommand({ kind: 'capture', text: str(args.text) }, env)
          else if (tc.name === 'player') {
            const action = (str(args.action) || 'play') as 'play' | 'pause' | 'next' | 'previous'
            const what = str(args.what)
            if (action === 'play' && (what || str(args.device)))
              await runVoiceCommand(
                {
                  kind: 'play',
                  what,
                  sort: (str(args.kind) || undefined) as 'track' | undefined,
                  device: str(args.device) || undefined,
                },
                env,
              )
            else await runVoiceCommand({ kind: 'transport', action }, env)
          } else if (tc.name === 'focus')
            await runVoiceCommand({ kind: 'pomodoro', action: (str(args.action) || 'start') as 'start' }, env)
          else if (tc.name === 'open') await runVoiceCommand({ kind: 'open', view: str(args.view) }, env)
          else if (tc.name === 'task') {
            const a = str(args.action)
            await runVoiceCommand({ kind: a === 'done' ? 'done' : a === 'start' ? 'start' : 'whatnext' }, env)
          } else if (tc.name === 'home') {
            const r = await api.homeStates([])
            const n = str(args.name).toLowerCase()
            const hit = r.entities.filter((e) => e.toggle).find((e) => e.name.toLowerCase().includes(n))
            if (hit) {
              await api.homeCall(hit.id, str(args.service) || 'toggle')
              said = `${str(args.service) || 'toggle'} ${hit.name}`
            } else said = `No light or switch called ${str(args.name)}.`
          } else said = `no such tool: ${tc.name}`
        } catch (e) {
          said = `failed: ${e instanceof Error ? e.message : String(e)}`
        }
        results.push({ tool_call_id: tc.id, content: said || 'done' })
      }
      try {
        useStream.getState().markSent(session.id)
        await api.agentToolResults(session.id, results)
        await engine.sync()
      } catch (e) {
        setErr((e as Error).message)
      }
    })()
  }, [session, sentHere, messages, today, projects, navigate])

  if (asleep)
    return (
      <AgentsLayout currentId={id}>
        <PrimeAsleep key={ask} ask={ask} factory={factory.on} />
      </AgentsLayout>
    )
  // on its way to Prime's thread, or not yet knowing whether it runs: no empty chat to type into
  if (!id && !search.fresh && !search.quick) return <AgentsLayout currentId={id}>{null}</AgentsLayout>
  // the quick chat lights its own row in the agents column, whichever workspace's thread this is
  const listId = session?.spec.prime || search.quick ? QUICK_CHAT_ID : id
  return (
    <AgentsLayout currentId={listId}>
      {/* on a desk the chat fills the window: its composer sits at the bottom, as in Prime's thread */}
      <div className="flex flex-col md:min-h-[calc(100dvh-3rem)]">
        <header className="flex items-center gap-2 mb-4">
          <h1 className="flex-1 min-w-0 text-lg font-semibold tracking-tight truncate">
            {session?.spec.prime ? QUICK_CHAT : (session?.spec.title ?? 'New chat')}
          </h1>
          {session && (
            <IconButton
              label="New chat"
              onClick={() => void navigate({ to: '/agents', search: { fresh: true } as never })}
            >
              <Plus size={17} aria-hidden />
            </IconButton>
          )}
          <span className="lg:hidden">
            <IconButton label="Agents and chats" onClick={() => setShowHistory((v) => !v)}>
              <History size={17} aria-hidden />
            </IconButton>
          </span>
        </header>

        {/* the coding agents belong to the work threads, not to a chat about the dentist */}

        <SideSheet open={showHistory} onClose={() => setShowHistory(false)} title="Agents and chats">
          <AgentList currentId={listId} onPick={() => setShowHistory(false)} />
          <ul className="flex flex-col gap-1 mt-4">
            {session && (
              <li className="pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowHistory(false)
                    void mutate.action(session.id, archived ? 'restore' : 'archive')
                    if (!archived) void navigate({ to: '/agents', search: { fresh: true } as never })
                  }}
                  className="text-xs text-muted hover:text-fg min-h-9 px-1"
                >
                  {archived ? 'Restore this chat' : 'Archive this chat'}
                </button>
              </li>
            )}
          </ul>
        </SideSheet>

        <div className="flex-1 pb-[calc(14.5rem+var(--player-h,0px))] md:pb-0">
          {messages.length === 0 && !working && (
            <div className="pt-6 pb-4" data-testid="chat-empty">
              <p className="text-[15px] text-muted mb-1">What are we doing?</p>
              <p className="text-xs text-faint mb-3">Type / for the short ways to ask.</p>
              <div className="flex flex-wrap gap-2">
                {CHAT_COMMANDS.filter((c) => c.prompt)
                  .slice(0, 4)
                  .map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => run(c)}
                      className="rounded-full bg-raised border border-line px-3 min-h-9 text-sm hover:border-line-strong"
                    >
                      {c.hint}
                    </button>
                  ))}
              </div>
            </div>
          )}
          <ul className="flex flex-col gap-4" data-testid="agent-turns">
            {messages.map((m, i) => (
              <Turn key={i} m={m} titleOf={titleOf} acts={settings.agent_acts && sentHere} />
            ))}
            {(working || (!!streamed && !settled)) && (
              <li data-testid="agent-working">
                {streamed ? (
                  <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
                    {streamed}
                    <span
                      className="inline-block w-1.5 h-4 -mb-0.5 ml-0.5 bg-accent animate-pulse"
                      aria-hidden
                    />
                  </p>
                ) : (
                  <p className="text-sm text-faint animate-pulse">
                    {step || session?.status.step || 'Thinking…'}
                  </p>
                )}
              </li>
            )}
            {session?.status.last_error && !working && (
              <li className="text-xs text-rose-300">{session.status.last_error}</li>
            )}
          </ul>
        </div>

        {/* the composer clears the raised centre button, so the thread would show through the gap under it */}
        {!keyboardUp && (
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-10 pointer-events-none bg-gradient-to-t from-ink from-60% to-transparent"
            style={{ height: 'calc(13rem + env(safe-area-inset-bottom))' }}
            aria-hidden
          />
        )}
        {/* composer: above the tab bar on the phone, above the keyboard while typing, inline on desktop */}
        <div
          className="kb-follow fixed inset-x-3 z-20 md:sticky md:bottom-3! md:mt-4 md:inset-auto"
          style={{
            // the tab bar steps aside while typing, so its height is not a floor then
            bottom: keyboardUp ? 'calc(var(--vv-gap) + 0.5rem)' : 'calc(var(--above-tabbar) + 0.75rem)',
          }}
        >
          {session?.labels.member ? (
            <p className="text-xs text-faint px-1" data-testid="member-thread-note">
              This is {session.spec.title.replace(/ and Prime$/, '')}'s own thread with her Prime. You can
              read it here; Prime tells her things with tell_member, and its answers to her are its own.
            </p>
          ) : session?.labels.client ? (
            <p className="text-xs text-faint px-1" data-testid="client-thread-note">
              What a Claude Code session holding this key proposes and asks. Nothing changes until you tap; it
              reads your answers itself. Revoke the key in Settings, API tokens.
            </p>
          ) : enabled === false ? (
            <p className="text-xs text-faint px-1">
              The server has no assistant configured. Past chats are still readable.
            </p>
          ) : (
            <PrimeComposer
              compact
              // a new chat has no session to carry pictures yet (start takes words only)
              pictures={!!session && !archived}
              busy={sending}
              disabled={archived}
              value={text}
              onValueChange={setText}
              onSend={send}
              onError={setErr}
              commands={CHAT_COMMANDS}
              onCommand={run}
              onMic={voice.available ? () => void voice.start().catch(() => {}) : undefined}
              placeholder={archived ? 'Restore this chat to carry on' : 'Say what you need…'}
              inputTestId="agent-input"
              sendTestId="agent-send"
              sendLabel="Send"
              className="bg-panel/95"
            />
          )}
          {archived && session && (
            <div className="mt-2 flex justify-end">
              <Button variant="chip" onClick={() => void mutate.action(session.id, 'restore')}>
                Restore this chat
              </Button>
            </div>
          )}
          {err && <p className="text-xs text-rose-300 mt-1 px-1">{err}</p>}
        </div>
      </div>
    </AgentsLayout>
  )
}

/**
 * The factory's news about a coding agent, as Prime got it: a line in the
 * thread (not your bubble) with the agent's live phase and a way in.
 */
const AgentNews = ({ m }: { m: AgentMessage }) => {
  const run = useResource(m.agent_run ?? '') as AgentRunResource | undefined
  // its first line, never the framing that tells the model it is not the owner's words
  const what = newsParts(appLineOf(m.content)?.body ?? m.content).first
  return (
    <li data-testid="agent-news">
      <Link
        to="/agents/run/$id"
        params={{ id: m.agent_run ?? '' }}
        className="flex items-start gap-2 rounded-[var(--radius-card)] border border-line bg-panel/60 px-3 py-2 text-sm hover:bg-raised"
      >
        <Code2 size={15} className="mt-0.5 shrink-0 text-muted" aria-hidden />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span className="font-semibold truncate">{run?.spec.title || 'A coding agent'}</span>
            {run && <RunTag run={run} />}
          </span>
          <span className="block text-xs text-muted mt-0.5 line-clamp-2">{what}</span>
        </span>
      </Link>
    </li>
  )
}

/** The tools a turn called, in a line; each opens to what it was given (the answer is the ↳ line after). */
const ToolCalls = ({ calls }: { calls: NonNullable<AgentMessage['tool_calls']> }) => {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <div className="pl-1" data-testid="agent-tool-calls">
      <div className="flex flex-wrap gap-x-3">
        {calls.map((tc) => (
          <button
            key={tc.id}
            type="button"
            onClick={() => setOpen(open === tc.id ? null : tc.id)}
            aria-expanded={open === tc.id}
            className="min-h-8 text-xs text-faint hover:text-fg"
          >
            {tc.name.replace(/_/g, ' ')}…
          </button>
        ))}
      </div>
      {calls.map(
        (tc) =>
          open === tc.id && (
            <div key={tc.id} className="mb-1">
              <ToolCallDetail
                call={{ name: tc.name, input: tc.args }}
                noResult="Its answer, if it has one, is the line under the reply."
              />
            </div>
          ),
      )}
    </div>
  )
}

/** One turn: your words on the right, the agent's as plain text on the left. */
export const Turn = ({
  m,
  titleOf,
  acts,
}: {
  m: AgentMessage
  titleOf: (id: string) => string | undefined
  /** when the owner has let the agent act, a proposal does itself */
  acts?: boolean
}) => {
  const suggestions = useSuggestionsByIds(m.suggestions ?? [])
  if (m.role === 'user' && m.agent_run) return <AgentNews m={m} />
  if (m.role === 'tool')
    return (
      <li className="text-xs text-faint pl-1" data-testid="agent-tool-result">
        ↳ {m.content}
      </li>
    )
  if (m.role === 'user') {
    const pictures = m.images ?? []
    // a message sent as pictures alone carries the server's stand-in words; the pictures say it
    const words =
      pictures.length > 0 && m.content === PICTURE_ONLY ? '' : m.from ? senderText(m.content) : m.content
    return (
      <li className="flex flex-col items-end gap-1.5">
        {pictures.length > 0 && (
          <div className="max-w-[85%]">
            <SessionPictures images={pictures} />
          </div>
        )}
        {(words || m.from || m.capture) && (
          <div
            className="max-w-[85%] rounded-br-sm bg-accent-soft text-fg px-3.5 py-2 text-[15px] whitespace-pre-wrap"
            style={{ borderRadius: 'var(--radius-bubble)', borderBottomRightRadius: '6px' }}
          >
            {m.channel === 'voice' && (
              <Mic size={12} className="inline mr-1 -mt-0.5 opacity-60" aria-label="spoken" />
            )}
            {(m.from || m.capture) && (
              <span className="block text-[13px] font-semibold opacity-60 mb-0.5" data-testid="agent-from">
                {m.from ? `From ${m.from}` : 'Capture'}
              </span>
            )}
            {words}
          </div>
        )}
      </li>
    )
  }
  return (
    <li className="flex flex-col gap-2">
      {m.content && <Reply text={m.content} />}
      {m.looked?.includes('tell_agent') && (
        <p className="text-xs text-faint pl-1" data-testid="agent-told">
          ↳ told the agent
        </p>
      )}
      {m.tool_calls && m.tool_calls.length > 0 && <ToolCalls calls={m.tool_calls} />}
      {suggestions.map((s) => (
        <Proposal key={s.id} s={s} titleOf={titleOf} acts={!!acts} />
      ))}
    </li>
  )
}

/**
 * What the agent wants to do, in the flow of the conversation rather than as a
 * form: a line per change and one button. With acting on it has already
 * happened and the button is Undo.
 */
/** A shared capture's content is the sender's words plus the owner's link note; the bubble shows the sender's. */
const senderText = (content: string) => {
  const i = content.lastIndexOf('\n\nContext from Alfie:')
  return i >= 0 ? content.slice(0, i) : content
}

/** The pull request a merge op is about, as its agent's id (for the review page). */
const useMergeRun = (s: SuggestionResource) => {
  const runs = useAllRuns() ?? []
  const op = s.spec.ops.find((o) => o.type === 'merge_pr')
  const a = (op?.args ?? {}) as { repo?: string; number?: number }
  if (!op || !a.repo || !a.number) return undefined
  // a repo URL may end in .git, and more than one session can carry the same
  // pull request (a second attempt): the one that knows it settled wins
  const repo = a.repo.replace(/\.git$/, '')
  const same = runs.filter(
    (r) => r.spec.pr_number === a.number && r.spec.repo_url.replace(/\.git$/, '').endsWith('/' + repo),
  )
  return same.find((r) => prSettled(r)) ?? same[0]
}

/** A merge card whose pull request has since merged or closed (anywhere) has nothing left to do. */
const prSettled = (run: { spec: { pr_state?: string } } | undefined) =>
  run?.spec.pr_state === 'merged' || run?.spec.pr_state === 'closed'

/**
 * Where a merge card stands in the queue, in one line: Queued (2), Merging…, or
 * the plain reason it failed. Never a spinner without an end: the server writes
 * every step on the card, and a failure is a sentence and a way to try again.
 */
export const MergeLine = ({ view, since }: { view: MergeView; since?: string }) => {
  if (view.state === 'idle' || view.state === 'merged') return null
  return view.state === 'merging' ? (
    <MergingLine since={since} />
  ) : (
    <p
      className={cx('text-sm', view.state === 'failed' ? 'text-fg-2' : 'text-muted')}
      role="status"
      data-testid="merge-state"
      data-merge-state={view.state}
    >
      {mergeWords(view)}
    </p>
  )
}

/** Merging… and how long: Forgejo can take a minute or two, and the card keeps counting until it says merged. */
const MergingLine = ({ since }: { since?: string }) => {
  const now = useNow(1000)
  return (
    <p className="text-sm text-muted" role="status" data-testid="merge-state" data-merge-state="merging">
      {mergingWords(since, now)}
    </p>
  )
}

/** When any card was last tapped (ms), shared by every card on the page. */
let lastCardTap = 0

/**
 * A card's taps go through here. For a moment after any card is tapped, every
 * card ignores taps: a second tap meant for the card just answered cannot land
 * on the one that slides up into its place, and a double tap (or iOS doubling a
 * click) does a merge once. A tap while the last one is still running is
 * dropped too.
 */
export const useCardTap = (settleMs = 700) => {
  const running = useRef(false)
  return async (fn: () => Promise<void>) => {
    const now = Date.now()
    if (running.current || now - lastCardTap < settleMs) return
    lastCardTap = now
    running.current = true
    try {
      await fn()
    } finally {
      running.current = false
    }
  }
}

const flashFailure = (e: unknown) =>
  useUi
    .getState()
    .showFlash(navigator.onLine ? `Could not do it: ${(e as Error).message}` : 'You are offline', 4000)

/**
 * Answer an ask with one of its buttons: the first does what the card carries,
 * any other puts it away; either way Prime hears the answer. The card and the
 * graph's approve and × share this.
 */
export const answerAsk = async (s: SuggestionResource, option: string, i: number): Promise<boolean> => {
  try {
    // the card keeps the answer (status.answer), so whoever asked can read it back
    if (i === 0 && s.spec.ops.length) await acceptSuggestion(s, undefined, option)
    else await mutate.bookkeeping(() => mutate.action(s.id, 'dismiss', { answer: option }))
    // Prime hears it as the owner's next message; a Claude Code client reads it from its cards
    if (s.spec.session_id && !s.labels.client)
      await mutate.action(s.spec.session_id, 'say', { text: `${option} (you asked: ${s.spec.title})` })
    return true
  } catch (e) {
    flashFailure(e)
    return false
  }
}

/** Do what a proposal carries (its "Do it"); an agent it starts gets its toast. */
export const acceptCard = (s: SuggestionResource): Promise<boolean> =>
  acceptSuggestion(s)
    .then((made) => {
      // the owner stays where they are: a tap on the toast is the way in
      const run = made.find((m) => m.kind === 'AgentRun')
      if (run) announceAgent(s, run.id)
      return true
    })
    .catch((e) => {
      flashFailure(e)
      return false
    })

/** Whose card it is, on one a Claude Code client made (its key's name, the owner's words). */
const ClientFrom = ({ s }: { s: SuggestionResource }) =>
  s.labels.client ? (
    <p className="text-[13px] text-faint mb-0.5" data-testid="card-from-client">
      From {s.labels.from || 'Claude Code'}
    </p>
  ) : null

/** The × in a waiting card's corner: put it away without answering. */
const CardClose = ({ s }: { s: SuggestionResource }) => (
  <span className="absolute right-1 top-1">
    <IconButton
      label="Dismiss"
      onClick={() =>
        void dismissSuggestion(s).catch((e: Error) => useUi.getState().showFlash(e.message, 3000))
      }
      data-testid="card-dismiss"
    >
      <X size={15} aria-hidden />
    </IconButton>
  </span>
)

/**
 * Prime asking with buttons: its question, a line of why, and an answer a tap
 * away. The first option does what the card carries (a merge, say); every tap
 * goes back to Prime as the owner's answer, so the thread carries on.
 */
const AskCard = ({
  s,
  titleOf,
  kindOf,
}: {
  s: SuggestionResource
  titleOf: (id: string) => string | undefined
  kindOf: (id: string) => string | undefined
}) => {
  const [busyOn, setBusyOn] = useState<{ option: string; key: string } | null>(null)
  const mergeRun = useMergeRun(s)
  const options = s.spec.options ?? []
  const gone = prSettled(mergeRun)
  const queue = useMergeCards()
  const mv = mergeView(s, queue)
  // a retry after a failure is a new tap: busy belongs to the failure it answered
  const busyKey = s.status.merge?.failed_at ?? ''
  const busy = busyOn?.key === busyKey ? busyOn.option : ''
  const setBusy = (option: string) => setBusyOn(option ? { option, key: busyKey } : null)
  const inQueue = mv.state === 'queued' || mv.state === 'merging' || mv.state === 'updating'
  const pending = s.status.state === 'pending' && !gone
  const merged =
    (gone && s.status.state === 'pending') ||
    (s.status.state === 'accepted' && s.spec.ops.some((op) => op.type === 'merge_pr'))
  const tap = useCardTap()
  const [fresh] = useState(() => isFresh(s.created_at, Date.now()))
  // stays busy once it worked: the card leaves pending by itself, and a second tap must not land
  const answer = (option: string, i: number) =>
    tap(async () => {
      setBusy(option)
      if (!(await answerAsk(s, option, i))) setBusy('')
    })
  return (
    <div
      className={cx('relative border border-accent/40 bg-panel/60 px-3 py-2.5 pr-10', fresh && 'card-in')}
      style={{ borderRadius: 'var(--radius-card)' }}
      data-suggestion-id={s.id}
      data-testid="agent-ask"
    >
      {/* one way to say no: with a choice of answers the last is it, so the × is only for a single one */}
      {pending && mv.state !== 'merging' && options.length < 2 && <CardClose s={s} />}
      <ClientFrom s={s} />
      <p className="font-semibold text-[15px] leading-snug">{s.spec.title}</p>
      {s.spec.rationale && <p className="text-sm text-muted mt-0.5">{s.spec.rationale}</p>}
      {s.spec.ops.length > 0 && (
        // what the first answer does, without saying its verb again ("Ship it: Ship …")
        <p className="text-xs text-faint mt-1">
          {s.spec.ops.map((op) => describeOp(op, titleOf, kindOf)).join('; ')}
        </p>
      )}
      {pending && <MergeLine view={mv} since={mergeStartedAt(s, queue)} />}
      {pending && inQueue ? (
        mergeRun && (
          <Link to="/dev/review/$id" params={{ id: mergeRun.id }} className={CHIP_CLASS}>
            Review
          </Link>
        )
      ) : pending ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {options.map((o, i) => (
            <Button
              key={o}
              variant={i === 0 ? 'primary' : 'chip'}
              onClick={() => void answer(o, i)}
              disabled={!!busy}
              data-testid="ask-option"
            >
              {busy === o
                ? mergeRun || s.spec.ops.some((op) => op.type === 'merge_pr')
                  ? 'Queuing…'
                  : 'Doing it…'
                : mv.state === 'failed' && i === 0
                  ? 'Try again'
                  : o}
            </Button>
          ))}
          {mergeRun && (
            <Link to="/dev/review/$id" params={{ id: mergeRun.id }} className={CHIP_CLASS}>
              Review first
            </Link>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-faint mt-1.5" data-testid={merged ? 'agent-merged' : undefined}>
            {merged ? mergedWords(s) : 'Answered'}
          </p>
          {merged && s.status.merge?.note && <p className="text-xs text-faint">{s.status.merge.note}</p>}
        </>
      )}
    </div>
  )
}

export const Proposal = ({
  s,
  titleOf,
  acts,
}: {
  s: SuggestionResource
  titleOf: (id: string) => string | undefined
  acts: boolean
}) => {
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const queue = useMergeCards()
  const mv = mergeView(s, queue)
  // a retry after a failure is a new tap: busy belongs to the failure it answered
  const busyKey = s.status.merge?.failed_at ?? ''
  const busy = busyOn === busyKey
  const setBusy = (on: boolean) => setBusyOn(on ? busyKey : null)
  const inQueue = mv.state === 'queued' || mv.state === 'merging' || mv.state === 'updating'
  const { kindOf } = useNames()
  const started = useRef(false)
  const [fresh] = useState(() => isFresh(s.created_at, Date.now()))
  const pending = s.status.state === 'pending'
  // a Claude Code client's card waits for the owner's tap whatever "let it act" says
  const acting = acts && !s.labels.client
  // starting a coding agent always waits for a tap, acting on or not
  const asks =
    !acting ||
    s.spec.ops.some((op) => op.type === 'start_agent' || op.type === 'merge_pr' || op.type === 'release')
  const mergeRun = useMergeRun(s)
  const tap = useCardTap()
  // every agent a card started gets its own way in
  const agentRuns = (s.status.created ?? []).filter((c) => c.kind === 'AgentRun')
  useEffect(() => {
    if (!acting || !pending || started.current) return
    started.current = true
    void applyForAgent(s).catch(() => {})
  }, [acting, pending, s])
  const lines = s.spec.ops.map((op, i) => (
    <li key={i} className="flex items-start gap-2 text-sm text-fg-2">
      <Check size={14} className="mt-1 shrink-0 text-accent-text" aria-hidden />
      <span>{describeOp(op, titleOf, kindOf)}</span>
    </li>
  ))
  if (s.spec.type === 'ask') return <AskCard s={s} titleOf={titleOf} kindOf={kindOf} />
  if (!s.spec.ops.length) return null
  // accepted here, or applied by the server for a capture (its receipt has the put-back)
  const merges = s.spec.ops.some((op) => op.type === 'merge_pr')
  const done =
    s.status.state === 'accepted' ||
    s.status.state === 'applied' ||
    (merges && s.status.state === 'pending' && prSettled(mergeRun))
  const filed = s.status.state === 'applied' || s.status.state === 'reverted'
  return (
    <div
      className={cx('relative border border-line bg-panel/60 px-3 py-2.5 pr-10', fresh && 'card-in')}
      style={{ borderRadius: 'var(--radius-card)' }}
      data-suggestion-id={s.id}
      data-state={s.status.state}
      data-testid={done ? 'agent-did' : 'agent-proposal'}
    >
      {pending && !done && mv.state !== 'merging' && <CardClose s={s} />}
      {s.labels.client && (
        <>
          <ClientFrom s={s} />
          <p className="font-semibold text-[15px] leading-snug mb-1">{s.spec.title}</p>
          {s.spec.rationale && <p className="text-sm text-muted mb-1.5">{s.spec.rationale}</p>}
        </>
      )}
      <ul className="flex flex-col gap-1">{lines}</ul>
      {pending && !done && <MergeLine view={mv} since={mergeStartedAt(s, queue)} />}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {filed ? (
          <Link
            to="/captures/$id"
            params={{ id: s.id }}
            className="text-xs text-muted hover:text-fg min-h-8 inline-flex items-center"
            data-testid="agent-filed"
          >
            {s.status.state === 'reverted' ? 'Put back' : 'Filed: see it or put it back'}
          </Link>
        ) : done && agentRuns.length > 0 ? (
          <span className="flex flex-wrap gap-2">
            {agentRuns.map((a) => (
              <Link
                key={a.id}
                to="/agents/run/$id"
                params={{ id: a.id }}
                className={CHIP_CLASS}
                data-testid="agent-open-run"
              >
                {agentRuns.length > 1 ? `Open ${titleOf(a.id) ?? 'the agent'}` : 'Open the agent'}
              </Link>
            ))}
          </span>
        ) : done && s.spec.ops.some((op) => op.type === 'merge_pr') ? (
          <span className="text-xs text-faint" data-testid="agent-merged">
            {mergedWords(s)}
            {s.status.merge?.note && <span className="block">{s.status.merge.note}</span>}
          </span>
        ) : done ? (
          <button
            type="button"
            onClick={() => void undoWithFlash()}
            className="text-xs text-muted hover:text-fg min-h-8"
            data-testid="agent-undo"
          >
            Undo
          </button>
        ) : pending && inQueue ? (
          mergeRun && (
            <Link to="/dev/review/$id" params={{ id: mergeRun.id }} className={CHIP_CLASS}>
              Review
            </Link>
          )
        ) : pending && asks ? (
          <>
            <Button
              variant="primary"
              onClick={() =>
                void tap(async () => {
                  setBusy(true)
                  // stays busy once it worked: the card leaves pending by itself
                  if (!(await acceptCard(s))) setBusy(false)
                })
              }
              disabled={busy}
              data-testid="suggestion-accept"
            >
              {busy ? (merges ? 'Queuing…' : 'Doing it…') : mv.state === 'failed' ? 'Try again' : 'Do it'}
            </Button>
            {mergeRun && (
              <Link to="/dev/review/$id" params={{ id: mergeRun.id }} className={CHIP_CLASS}>
                Review first
              </Link>
            )}
            {/* no "No thanks" beside it: the × in the corner is the one way to say no */}
          </>
        ) : (
          <span className="text-xs text-faint">
            {s.status.state === 'dismissed' ? 'Left alone' : 'Doing it…'}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * The agent's words. Anything past a phone screen folds: an answer you have to
 * scroll before you can act on it is one that does not get read.
 */
// a tool call the model wrote out as text instead of making it (the server
// now retries those, but older turns may hold one)
const WRITTEN_CALL = /<\s*(invoke|function_calls|parameter)\b/i

const Reply = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false)
  if (WRITTEN_CALL.test(text))
    return (
      <p className="text-sm text-faint" data-testid="agent-reply-failed">
        This reply failed. Ask again.
      </p>
    )
  // a reply with a diagram is shown whole: cutting it would cut the fence
  const long = text.length > 420 && !text.includes('```')
  if (!long || open)
    return <MdLite text={text} className="text-[15px] leading-relaxed" testId="agent-reply" />
  // cut at a sentence end where there is one, so the fold reads as a thought
  const head = text.slice(0, 320)
  const stop = Math.max(head.lastIndexOf('. '), head.lastIndexOf('\n'))
  return (
    <div data-testid="agent-reply">
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
        {text.slice(0, stop > 120 ? stop + 1 : 320).trimEnd()}
      </p>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 text-xs text-accent-text hover:text-fg min-h-8"
        data-testid="agent-reply-more"
      >
        The rest of it
      </button>
    </div>
  )
}
